import React, { useState, useEffect } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../firebase';
import { LOAN_TYPE_NAMES } from '../forms/loanTypes';
import WhatsAppAPIIntegration from './WhatsAppAPIIntegration';
import whatsappAPI from '../services/whatsappAPI';

const WhatsAppMessage = ({ customer, documents }) => {
    const [copied, setCopied] = useState(false);

    const [selectedTemplate, setSelectedTemplate] = useState('documents');
    const [vehicleData, setVehicleData] = useState(null);

    const [showDealerSelection, setShowDealerSelection] = useState(false);
    const [showContactCard, setShowContactCard] = useState(false);

    const [allContacts, setAllContacts] = useState([]);
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [loadingContacts, setLoadingContacts] = useState(false);
    const [contactSearchTerm, setContactSearchTerm] = useState('');
    const [contactTypeFilter, setContactTypeFilter] = useState('all');
    const [showAPIIntegration, setShowAPIIntegration] = useState(false);

    // Direct sending state
    const [sending, setSending] = useState(false);
    const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0, current: '' });
    const [sendResults, setSendResults] = useState([]);

    const generateDocumentMessage = () => {
        const loanTypeName = LOAN_TYPE_NAMES[customer.loanType] || customer.loanType;

        // Get loan type and employment type to fetch required documents
        const effectiveLoanType = customer.loanType ||
            customer.loan_application?.loan_details?.loan_type ||
            customer.customer_details?.basic_info?.loan_type ||
            '';

        let effectiveEmployment = customer.employmentType ||
            customer.occupationType ||
            customer.customer_details?.occupation_info?.type ||
            customer.customer_details?.basic_info?.occupation_type ||
            '';

        if (customer.customer_details?.farm_income_info?.has_farm) {
            effectiveEmployment = 'FARMER';
        }

        // Import getDocumentList function
        const { getDocumentList } = require('../forms/loanTypes');

        // Get required documents list based on loan type
        let requiredDocs = getDocumentList(effectiveLoanType, effectiveEmployment);

        // Fallback if no documents found
        if (requiredDocs.length === 0) {
            requiredDocs = [
                'Aadhar Card',
                'PAN Card',
                'Passport Size Photo',
                'Bank Statement (6 Months)',
                'Income Proof',
                'Address Proof',
                'Signature'
            ];
        }

        // Get collected documents from customer form
        const collectedDocs = customer.loan_application?.documents?.collected_documents ||
            customer.documents?.collected_documents ||
            customer.collected_documents ||
            {};

        // Get extra documents
        const extraDocs = customer.loan_application?.documents?.extra_documents ||
            customer.documents?.extra_documents ||
            [];

        // Separate into collected and pending based on required docs
        let collectedList = [];
        let pendingList = [];

        // Check each required document
        requiredDocs.forEach(docName => {
            const dbKey = docName.replace(/[^a-zA-Z0-9]/g, '');
            const isCollected = collectedDocs[dbKey] === true || collectedDocs[dbKey] === 'true';

            if (isCollected) {
                collectedList.push(`✅ ${docName}`);
            } else {
                pendingList.push(`📌 ${docName}`);
            }
        });

        // Add extra documents
        extraDocs.forEach(doc => {
            if (doc.collected) {
                collectedList.push(`✅ ${doc.name}`);
            } else {
                pendingList.push(`📌 ${doc.name}`);
            }
        });

        // Build document list - Always Pending Only
        let docList = pendingList.length > 0 ? pendingList.join('\n') : 'All documents collected! ✅';

        const greeting = `🔔 *Reminder: Pending Documents*\n\nDear ${customer.name},\n\nThe following documents are still pending for your ${loanTypeName}:\n\n`;

        // Add birthdate info if available
        let birthdateInfo = '';
        if (vehicleData && vehicleData.birthdate) {
            const age = calculateAge(vehicleData.birthdate);
            const birthDate = new Date(vehicleData.birthdate).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
            birthdateInfo = `\n🎂 Date of Birth: ${birthDate} (Age: ${age} years)\n`;
        }

        const footer = `${birthdateInfo}\n\n📞 *Contact Details:*\n🏢 *Vardhaman Financial Services*\n\n☎️ Office - 9270355171\n\n *Thank you for your cooperation!*`;

        return greeting + docList + footer;
    };

    const generateVehicleLoanMessage = () => {
        // Use vehicle_info from customer object if available, otherwise fallback to vehicleData
        const vehicle = customer.vehicle_info || vehicleData || {};

        const basic = customer.customer_details?.basic_info || {};
        const occupation = customer.customer_details?.occupation_info || {};
        const loanApp = customer.loan_application?.loan_details || {};
        const existingLoans = customer.existing_loans || {};
        const homeLoan = customer.home_loan_details || {};

        // --- Applicant Income Details ---
        let incomeDetails = '';
        if (occupation.type === 'Job') {
            const job = occupation.employment_details || {};
            incomeDetails = `*Applicant Income Details:*
Type: Salaried
Company: ${job.company_name || 'N/A'}
Monthly Salary: ${job.monthly_salary ? `₹${job.monthly_salary}` : 'N/A'}`;
        } else if (occupation.type === 'Business') {
            const biz = occupation.employment_details || {};
            incomeDetails = `*Applicant Income Details:*
Type: Business
Business Name: ${biz.business_name || 'N/A'}
Rent Amount: ${biz.business_rent_amount ? `₹${biz.business_rent_amount}` : 'N/A'}
Income/Turnover: ${biz.itr_income ? `₹${biz.itr_income}` : (biz.annual_turnover_1 ? `₹${biz.annual_turnover_1}` : 'N/A')}`;
        } else if (occupation.type) {
            incomeDetails = `*Applicant Income Details:*
Type: ${occupation.type}`;
        } else {
            incomeDetails = `*Applicant Income Details:* N/A`;
        }

        // --- Co-Applicant Details ---
        let coApplicantDetails = '';
        if (homeLoan.coApplicants && Array.isArray(homeLoan.coApplicants) && homeLoan.coApplicants.length > 0) {
            const coApps = homeLoan.coApplicants.map((co, idx) => {
                let coIncome = 'N/A';
                if (co.income) coIncome = `₹${co.income}`;
                else if (co.monthlyIncome) coIncome = `₹${co.monthlyIncome}`;

                return `*Co-Applicant ${idx + 1}:* ${co.name || 'N/A'}
   Relation: ${co.relationship || 'N/A'}
   Occupation: ${co.occupation || 'N/A'}
   Income: ${coIncome}`;
            }).join('\n');
            coApplicantDetails = `\n${coApps}`;
        }

        // --- Active Loans & CIBIL ---
        let activeLoansText = 'None';
        const activeLoansData = (existingLoans.active_loans && typeof existingLoans.active_loans === 'object')
            ? Object.values(existingLoans.active_loans)
            : [];

        if (activeLoansData.length > 0) {
            activeLoansText = activeLoansData.map(loan =>
                `- ${loan.bankName || loan.bank || 'Bank'} (₹${loan.loanAmount || loan.amount || '0'}) - EMI: ₹${loan.emi || 'N/A'}`
            ).join('\n');
        }

        // CIBIL Score - Check multiple possible paths
        let cibil = existingLoans.cibil_score || customer.cibil_score || basic.cibil_score;
        if (!cibil && activeLoansData.length > 0) {
            cibil = activeLoansData.find(l => l.cibil_score)?.cibil_score;
        }

        // --- Documents List ---
        let availableDocs = [];
        let requiredDocs = [];

        const formDocs = customer.loan_application?.documents?.collected_documents ||
            customer.documents?.collected_documents ||
            customer.collected_documents;

        if (formDocs) {
            Object.entries(formDocs).forEach(([name, isCollected]) => {
                const readableName = name.replace(/([A-Z])/g, ' $1').trim();
                if (isCollected === true || isCollected === 'true') {
                    availableDocs.push(`✅ ${readableName}`);
                } else if (isCollected === false || isCollected === 'false') {
                    requiredDocs.push(`❌ ${readableName}`);
                }
            });

            const extraDocs = customer.loan_application?.documents?.extra_documents ||
                customer.documents?.extra_documents;
            if (extraDocs && Array.isArray(extraDocs)) {
                extraDocs.forEach(doc => {
                    if (doc.collected) {
                        availableDocs.push(`✅ ${doc.name}`);
                    }
                });
            }
        }

        if (documents) {
            Object.entries(documents).forEach(([name, status]) => {
                const docName = name.replace(/([A-Z])/g, ' $1').trim();
                const isInAvailable = availableDocs.some(d => d.includes(docName));

                if (status.completed) {
                    if (!isInAvailable) {
                        availableDocs.push(`✅ ${docName}`);
                    }
                } else {
                    if (!isInAvailable) {
                        requiredDocs.push(`❌ ${docName}`);
                    }
                }
            });
        }

        const availableDocsText = availableDocs.length > 0 ? availableDocs.join('\n') : 'None';
        const requiredDocsText = requiredDocs.length > 0 ? requiredDocs.join('\n') : 'None';

        return `🚗 *Vehicle Loan Information*

Vehicle Name / Model : ${vehicle.model || vehicle.vehicleName || 'N/A'}
Vehicle Type         : ${vehicle.vehicle_type || vehicle.vehicleType || 'N/A'}
Manufacture Year     : ${vehicle.manufacturing_year || vehicle.manufactureYear || 'N/A'}
Number of Owners     : ${vehicle.number_of_owners || vehicle.ownerCount || 'N/A'}
Odometer Reading     : ${vehicle.km_reading || vehicle.odometerReading || 'N/A'}
Registration City    : ${vehicle.registration_city || vehicle.registrationCity || 'N/A'}
Proposed Loan Amount : ${vehicle.loan_amount_required || 'N/A'}
Vehicle Price        : ${vehicle.purchase_price || vehicle.new_vehicle_total || 'N/A'}

--------------------------------

*Customer Details*
Loan Category: ${loanApp.loan_category || 'N/A'}
Subcategory: ${loanApp.loan_subcategory || 'N/A'}

${incomeDetails}
${coApplicantDetails ? coApplicantDetails + '\n' : ''}
*Financial Profile*
CIBIL Score: ${cibil || 'N/A'}
Active Loans:
${activeLoansText}

*Document Checklist*
_Available Documents:_
${availableDocsText}

_Required Documents:_
${requiredDocsText}

Please check and confirm:
* Maximum Loan Amount
* Tenure
* Interest Rate
* Any Remarks

📞 Contact Details:
Vardhaman Financial Services
Office - 9270355171

 Thank you for your cooperation!`;
    };

    const generatePropertyLoanMessage_Deprecated = () => {
        // Use home_loan_details or assets from customer object
        const property = customer.home_loan_details || customer.assets || {};

        const basic = customer.customer_details?.basic_info || {};
        const occupation = customer.customer_details?.occupation_info || {};
        const loanApp = customer.loan_application?.loan_details || {};
        const existingLoans = customer.existing_loans || {};
        const homeLoan = customer.home_loan_details || {};

        // --- Property Details ---
        let propertyDetails = '';
        if (homeLoan.propertyAddress || homeLoan.propertyArea || homeLoan.propertyType) {
            propertyDetails = `*Property Details*
Property Type: ${homeLoan.propertyType || 'N/A'}
Property Address: ${homeLoan.propertyAddress || 'N/A'}
Property Area: ${homeLoan.propertyArea || 'N/A'} sq.ft
Property Value: ${homeLoan.propertyValue ? `₹${homeLoan.propertyValue}` : 'N/A'}
Required Loan Amount: ${homeLoan.loanAmount || homeLoan.loan_amount || 'N/A'}

--------------------------------

`;
        }

        // --- Applicant Income Details ---
        let incomeDetails = '';
        if (occupation.type === 'Job') {
            const job = occupation.employment_details || {};
            incomeDetails = `*Applicant Income Details:*
Type: Salaried
Company: ${job.company_name || 'N/A'}
Monthly Salary: ${job.monthly_salary ? `₹${job.monthly_salary}` : 'N/A'}`;
        } else if (occupation.type === 'Business') {
            const biz = occupation.employment_details || {};
            incomeDetails = `*Applicant Income Details:*
Type: Business
Business Name: ${biz.business_name || 'N/A'}
Rent Amount: ${biz.business_rent_amount ? `₹${biz.business_rent_amount}` : 'N/A'}
Income/Turnover: ${biz.itr_income ? `₹${biz.itr_income}` : (biz.annual_turnover_1 ? `₹${biz.annual_turnover_1}` : 'N/A')}`;
        } else if (occupation.type) {
            incomeDetails = `*Applicant Income Details:*
Type: ${occupation.type}`;
        } else {
            incomeDetails = `*Applicant Income Details:* N/A`;
        }

        // --- Co-Applicant Details ---
        let coApplicantDetails = '';
        if (homeLoan.coApplicants && Array.isArray(homeLoan.coApplicants) && homeLoan.coApplicants.length > 0) {
            const coApps = homeLoan.coApplicants.map((co, idx) => {
                let coIncome = 'N/A';
                if (co.income) coIncome = `₹${co.income}`;
                else if (co.monthlyIncome) coIncome = `₹${co.monthlyIncome}`;

                return `*Co-Applicant ${idx + 1}:* ${co.name || 'N/A'}
   Relation: ${co.relationship || 'N/A'}
   Occupation: ${co.occupation || 'N/A'}
   Income: ${coIncome}`;
            }).join('\n');
            coApplicantDetails = `\n${coApps}\n`;
        }

        // --- Active Loans & CIBIL ---
        let activeLoansText = 'None';
        const activeLoansData = (existingLoans.active_loans && typeof existingLoans.active_loans === 'object')
            ? Object.values(existingLoans.active_loans)
            : [];

        if (activeLoansData.length > 0) {
            activeLoansText = activeLoansData.map(loan =>
                `- ${loan.bankName || loan.bank || 'Bank'} (₹${loan.loanAmount || loan.amount || '0'}) - EMI: ₹${loan.emi || 'N/A'}`
            ).join('\n');
        }

        let cibil = existingLoans.cibil_score || customer.cibil_score || basic.cibil_score;
        if (!cibil && activeLoansData.length > 0) {
            cibil = activeLoansData.find(l => l.cibil_score)?.cibil_score;
        }

        // --- Documents List (Property-specific) ---
        let availableDocs = [];
        let requiredDocs = [];

        const formDocs = customer.loan_application?.documents?.collected_documents ||
            customer.documents?.collected_documents ||
            customer.collected_documents;

        if (formDocs) {
            Object.entries(formDocs).forEach(([name, isCollected]) => {
                const readableName = name.replace(/([A-Z])/g, ' $1').trim();
                if (isCollected === true || isCollected === 'true') {
                    availableDocs.push(`✅ ${readableName}`);
                } else if (isCollected === false || isCollected === 'false') {
                    requiredDocs.push(`❌ ${readableName}`);
                }
            });

            const extraDocs = customer.loan_application?.documents?.extra_documents ||
                customer.documents?.extra_documents;
            if (extraDocs && Array.isArray(extraDocs)) {
                extraDocs.forEach(doc => {
                    if (doc.collected) {
                        availableDocs.push(`✅ ${doc.name}`);
                    }
                });
            }
        }

        if (documents) {
            Object.entries(documents).forEach(([name, status]) => {
                const docName = name.replace(/([A-Z])/g, ' $1').trim();
                const isInAvailable = availableDocs.some(d => d.includes(docName));

                if (status.completed) {
                    if (!isInAvailable) {
                        availableDocs.push(`✅ ${docName}`);
                    }
                } else {
                    if (!isInAvailable) {
                        requiredDocs.push(`❌ ${docName}`);
                    }
                }
            });
        }

        const availableDocsText = availableDocs.length > 0 ? availableDocs.join('\n') : 'None';
        const requiredDocsText = requiredDocs.length > 0 ? requiredDocs.join('\n') : 'None';

        return `🏠 *Property Loan Information*

${propertyDetails}*Customer Details*
Loan Category: ${loanApp.loan_category || 'N/A'}
Subcategory: ${loanApp.loan_subcategory || 'N/A'}

${incomeDetails}
${coApplicantDetails}
*Financial Profile*
CIBIL Score: ${cibil || 'N/A'}
Active Loans:
${activeLoansText}

*Document Checklist*
_Available Documents:_
${availableDocsText}

_Required Documents:_
${requiredDocsText}

Please check and confirm:
* Maximum Loan Amount
* Tenure
* Interest Rate
* Any Remarks

📞 Contact Details:
Vardhaman Financial Services
Office - 9270355171

 Thank you for your cooperation!`;
    };


    const generateBirthdayWishesMessage = () => {
        return `✨ Warm Birthday Wishes! 🎉🎂

On your special day, we wish you
good health, lasting happiness, and continued success.

May the coming year be filled with
🌼 peace of mind,
🌟 positive moments, and
🚀 new opportunities that lead you forward with confidence.

Thank you for being a valued part of our journey.
We look forward to serving you with trust and commitment in the years ahead.

Have a wonderful birthday and a truly prosperous year! ✨

Warm regards,
Vardhaman Financial Services

Office - 9270355171`;
    };


    const generatePropertyLoanMessage = () => {
        const basic = customer.customer_details?.basic_info || {};
        const occupation = customer.customer_details?.occupation_info || {};
        const business = occupation?.employment_details || {};
        const residence = customer.customer_details?.residence_info || {};
        const existingLoans = customer.existing_loans || {};
        const loanApp = customer.loan_application?.loan_details || {};
        // Assuming property details might be in a specific field, if not found, use placeholders
        const property = customer.property_info || {};

        // Get active loans
        const activeLoansData = (existingLoans.active_loans && typeof existingLoans.active_loans === 'object')
            ? Object.values(existingLoans.active_loans)
            : [];

        // CIBIL Score
        let cibil = existingLoans.cibil_score || customer.cibil_score || '';

        // Format active loans
        let activeLoansText = '';
        if (activeLoansData.length > 0) {
            activeLoansData.forEach((loan, idx) => {
                activeLoansText += `1. Finance Company Name: ${loan.bankName || loan.bank || '___________'}
2. Loan Type: ${loan.loanType || loan.type || '___________'}
3. Loan Amount (₹): ${loan.loanAmount || loan.amount || '___________'}
4. EMI (₹): ${loan.emi || '___________'}
5. Tenure: ${loan.tenure || '___________'}
6. Outstanding Amount (₹): ${loan.outstandingAmount || loan.outstanding || loan.paidAmount || '___________'}\n\n`;
            });
        }

        // Income details - only show if data exists
        let incomeSection = '';
        if (occupation.type === 'Job' || occupation.type === 'Salaried' || occupation.type === 'salaried') {
            const job = occupation.employment_details || {};
            // Only show if at least one field has data
            if (job.company_name || job.designation || job.years_employed || job.work_experience || job.monthly_salary || job.gross_salary) {
                incomeSection = `*Nature of Income:* Salaried

1. Company Name: ${job.company_name || '___________'}
2. Designation: ${job.designation || '___________'}
3. Total Work Experience: ${job.years_employed || job.work_experience || '___________'}
4. Job Type: ${job.job_type || 'Permanent / Temporary'}
5. Gross Salary (₹): ${job.gross_salary || '___________'}
6. Net Salary (₹): ${job.monthly_salary || job.net_salary || '___________'}
7. Form 16: ${job.has_form16 ? 'Yes' : 'No'}`;
            }
        } else if (occupation.type === 'Business' || occupation.type === 'Self Employed' || occupation.type === 'self_employed') {
            // Only show if at least one field has data
            if (business.business_name || business.business_type || business.years_in_business || business.yearly_income || business.itr_income || business.yearly_turnover) {
                incomeSection = `*Nature of Income:* Business

1. Name of Business: ${business.business_name || '___________'}
2. Type of Business: ${business.business_type || '___________'}
3. Business Experience (Years): ${business.years_in_business || '___________'}
4. Business Premises: ${business.business_place_type || 'Rented / Owned'}
5. Yearly Income (₹): ${business.itr_income || business.yearly_income || business.yearly_turnover || '___________'}
6. ITR (Last 3 Years): ${business.itr_filed ? 'Yes' : 'No'}
7. Udyam Registration: ${business.has_udyam_reg ? 'Yes' : 'No'}
8. Shop Act License: ${business.has_shop_act ? 'Yes' : 'No'}`;
            }
        }

        // Farm info check - only show if has farm data
        const farmIncome = customer.customer_details?.farm_income_info || {};
        const hasAgriIncome = farmIncome.has_farm || false;
        const agriSection = hasAgriIncome ? `

*Agriculture Income:* Yes
1. Land Area: ${farmIncome.farm_area || farmIncome.total_area || '___________'}
2. Owner Name: ${farmIncome.farm_owner_name || farmIncome.owner_name || '___________'}` : '';


        return `🏠 *LOAN PROPOSAL - PROPERTY LOAN* 🏠

*Loan Type:* ${loanApp.loan_subcategory || 'Home Loan / LAP / Mortgage'}
*Purpose:* ${loanApp.loan_purpose || 'Purchase / Construction / Renovation'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🏠 *PROPERTY DETAILS*

1. *Property Type:* ${property.property_type || 'Flat / House / Plot / Commercial'}
2. *Property Area:* ${property.area || '___________'}
3. *Property Value (Approx ₹):* ${property.valuation || '___________'}
4. *Property Address:* ${property.address || '___________'}
5. *Ownership:* ${property.ownership || 'Freehold / Leasehold'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

👤 *CUSTOMER DETAILS*

1. *City:* ${basic.city_village || customer.city || '___________'}
2. *House Ownership:* ${residence.ownership_type || 'Owned / Rented'}
${residence.ownership_type === 'Owned' || residence.ownership_type === 'Own' ? `   *Owner Name:* ${residence.house_owner_name || '___________'}` : ''}
3. *Driving License Type:* ${customer.vehicle_info?.has_tr_license ? 'TR' : customer.vehicle_info?.has_heavy_license ? 'Heavy' : 'TR / Heavy'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

💼 *INCOME DETAILS*

${incomeSection}
${agriSection}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

💳 *ACTIVE LOANS & CIBIL*

*CIBIL Score:* ${cibil || '___________'}

*Existing Loans:*
${activeLoansText || `1. Finance Company Name: ___________
2. Loan Type: ___________
3. Loan Amount (₹): ___________
4. EMI (₹): ___________
5. Tenure: ___________
6. Outstanding Amount (₹): ___________`}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📍 *REQUEST:*
Please review the above details and confirm the eligible loan amount and tenure.
Kindly update at the earliest possible.

📞 *Contact Details:*
🏢 *Vardhaman Financial Services*
☎️ Office - 9270355171`;
    };


    const generateCarLoanProposal = (vehicleType = 'new') => {
        const vehicle = customer.vehicle_info || vehicleData || {};
        const basic = customer.customer_details?.basic_info || {};
        const occupation = customer.customer_details?.occupation_info || {};
        const business = occupation?.employment_details || {};
        const residence = customer.customer_details?.residence_info || {};
        const existingLoans = customer.existing_loans || {};
        const loanApp = customer.loan_application?.loan_details || {};
        const vehicleHistory = customer.vehicle_history || { vehicles: [] };

        // Extract vehicle history data (handle both array and object formats)
        const getVehicleHistoryData = () => {
            const vehicles = vehicleHistory.vehicles;
            if (Array.isArray(vehicles) && vehicles.length > 0) {
                return vehicles[0];
            }
            if (vehicles && typeof vehicles === 'object') {
                const firstVehicle = Object.values(vehicles)[0];
                return firstVehicle || {};
            }
            return {};
        };
        const firstVehicle = getVehicleHistoryData();

        // Determine vehicle type based on parameter
        const vehicleTypeText = vehicleType === 'used' ? 'Used Car Loan' : 'New Car Loan';

        // Get active loans
        const activeLoansData = (existingLoans.active_loans && typeof existingLoans.active_loans === 'object')
            ? Object.values(existingLoans.active_loans)
            : [];

        console.log('Active Loans Debug:', activeLoansData);

        // CIBIL Score
        let cibil = existingLoans.cibil_score || customer.cibil_score || '';

        // Format active loans
        let activeLoansText = '';
        if (activeLoansData.length > 0) {
            activeLoansData.forEach((loan, idx) => {
                activeLoansText += `
Loan ${idx + 1}:
1. Finance Company Name: ${loan.bankName || loan.bank || '___________'}
2. Loan Type: ${loan.loanType || loan.type || '___________'}
3. Loan Amount (₹): ${loan.loanAmount || loan.amount || '___________'}
4. EMI (₹): ${loan.emi || '___________'}
5. Tenure: ${loan.tenure || '___________'}
6. Outstanding Amount (₹): ${loan.outstandingAmount || loan.outstanding || loan.paidAmount || '___________'}
`;
            });
        }

        // Income details based on occupation type - only show if data exists
        let incomeSection = '';
        if (occupation.type === 'Job' || occupation.type === 'Salaried' || occupation.type === 'salaried') {
            const job = occupation.employment_details || {};
            // Only show if at least one field has data
            if (job.company_name || job.designation || job.years_employed || job.work_experience || job.monthly_salary || job.gross_salary) {
                incomeSection = `Income Type: Salaried

1. Company Name: ${job.company_name || '___________'}
2. Designation: ${job.designation || '___________'}
3. Work Experience (Years): ${job.years_employed || job.work_experience || '___________'}
4. Job Type: ${job.job_type || 'Permanent / Temporary'}
5. Gross Salary (₹): ${job.gross_salary || '___________'}
6. Net Salary (₹): ${job.monthly_salary || job.net_salary || '___________'}
7. Form 16: ${job.has_form16 ? 'Yes' : 'No'}`;
            }
        } else if (occupation.type === 'Business' || occupation.type === 'Self Employed' || occupation.type === 'self_employed') {
            // Only show if at least one field has data
            if (business.business_name || business.business_type || business.years_in_business || business.yearly_income || business.itr_income || business.yearly_turnover) {
                incomeSection = `Income Type: Business

1. Name of Business: ${business.business_name || '___________'}
2. Type of Business: ${business.business_type || '___________'}
3. Business Experience (Years): ${business.years_in_business || '___________'}
4. Business Premises: ${business.business_place_type || 'Rented / Owned'}
5. Yearly Income (₹): ${business.itr_income || business.yearly_income || business.yearly_turnover || '___________'}
6. ITR (Last 3 Years): ${business.itr_filed ? 'Yes' : 'No'}
7. Udyam Registration: ${business.has_udyam_reg ? 'Yes' : 'No'}
8. Shop Act License: ${business.has_shop_act ? 'Yes' : 'No'}`;
            }
        }

        // Agriculture income - only show if has farm data
        const farmIncome = customer.customer_details?.farm_income_info || {};
        const hasAgriIncome = farmIncome.has_farm || false;
        const agriSection = hasAgriIncome ? `

Agriculture Income: Yes
1. Land Area: ${farmIncome.farm_area || farmIncome.total_area || '___________'}
2. Owner Name: ${farmIncome.farm_owner_name || farmIncome.owner_name || '___________'}` : '';

        return `🚗 *LOAN PROPOSAL - CAR LOAN* 🚗

*Loan Type:* ${vehicleTypeText}${vehicleType === 'used' && loanApp.loan_purpose ? `
*Purpose:* ${loanApp.loan_purpose}` : ''}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🚗 *VEHICLE DETAILS*
${vehicleType === 'used' ? `
1. *Registration Number:* ${vehicle.registration_number || '___________'}
2. *Vehicle Name:* ${vehicle.new_vehicle_type || vehicle.model || '___________'}
3. *Vehicle Model:* ${vehicle.new_vehicle_model || vehicle.model || '___________'}
4. *Manufacturing Year:* ${vehicle.manufacturing_year || '___________'}
5. *Fuel Type:* ${vehicle.fuel_type || 'Petrol / Diesel / Petrol + CNG / Electric'}
6. *Number of Owners:* ${vehicle.number_of_owners || '___________'}
7. *KM Reading (Odometer):* ${vehicle.km_reading || '___________'}
8. *Purchase Price (₹):* ${vehicle.purchase_price || vehicle.new_vehicle_invoice_price || vehicle.new_vehicle_total || '___________'}
9. *Loan Amount Required (₹):* ${vehicle.loan_amount_required || loanApp.loan_amount || '___________'}` : `
1. *Vehicle Name:* ${vehicle.new_vehicle_type || vehicle.model || '___________'}
2. *Vehicle Model:* ${vehicle.new_vehicle_model || vehicle.model || '___________'}
3. *Fuel Type:* ${vehicle.fuel_type || 'Petrol / Diesel / Petrol + CNG / Electric'}
4. *Purchase Price (₹):* ${vehicle.purchase_price || vehicle.new_vehicle_invoice_price || vehicle.new_vehicle_total || '___________'}`}

${vehicleType === 'used' ? `
10. *Hypothecation:* ${vehicle.hypothecation === 'yes' || vehicle.has_hypothecation ? 'Yes' : vehicle.hypothecation === 'no' ? 'No' : 'Yes / No'}
${vehicle.hypothecation === 'yes' || vehicle.has_hypothecation ? `   • *Bank / Finance Name:* ${vehicle.hypothecation_bank_name || '___________'}
   • *Balance Amount (₹):* ${vehicle.hypothecation_balance_amount || '___________'}` : `   • *Bank / Finance Name:* ___________
   • *Balance Amount (₹):* ___________`}

11. *Insurance:* ${vehicle.insurance === 'yes' || vehicle.new_vehicle_insurance ? 'Yes' : vehicle.insurance === 'no' ? 'No' : 'Yes / No'}
${vehicle.insurance === 'yes' || vehicle.new_vehicle_insurance ? `   • *IDV Amount (₹):* ${vehicle.insurance_idv_amount || vehicle.new_vehicle_insurance || '___________'}` : `   • *IDV Amount (₹):* ___________`}` : `

5. *Hypothecation:* ${vehicle.hypothecation === 'yes' || vehicle.has_hypothecation ? 'Yes' : vehicle.hypothecation === 'no' ? 'No' : 'Yes / No'}
${vehicle.hypothecation === 'yes' || vehicle.has_hypothecation ? `   • *Bank / Finance Name:* ${vehicle.hypothecation_bank_name || '___________'}
   • *Balance Amount (₹):* ${vehicle.hypothecation_balance_amount || '___________'}` : `   • *Bank / Finance Name:* ___________
   • *Balance Amount (₹):* ___________`}

6. *Insurance:* ${vehicle.insurance === 'yes' || vehicle.new_vehicle_insurance ? 'Yes' : vehicle.insurance === 'no' ? 'No' : 'Yes / No'}
${vehicle.insurance === 'yes' || vehicle.new_vehicle_insurance ? `   • *IDV Amount (₹):* ${vehicle.insurance_idv_amount || vehicle.new_vehicle_insurance || '___________'}` : `   • *IDV Amount (₹):* ___________`}`}


━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

👤 *CUSTOMER DETAILS*

1. *City:* ${basic.city_village || customer.city || '___________'}
2. *House Ownership:* ${residence.ownership_type || 'Owned / Rented'}
${residence.ownership_type === 'Owned' || residence.ownership_type === 'Own' ? `   *Owner Name:* ${residence.house_owner_name || '___________'}` : ''}
3. *Address Different from Aadhaar:* ${residence.different_from_aadhar ? 'Yes' : 'Yes / No'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

💼 *INCOME DETAILS*

${incomeSection}
${agriSection}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🚘 *OLD VEHICLE HISTORY*

1. *Vehicle Name:* ${firstVehicle?.model || '___________'}
2. *Finance Company Name:* ${firstVehicle?.financeCompany || '___________'}
3. *Loan Active?:* ${firstVehicle?.loanActive || '___________'}


━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

💳 *ACTIVE LOANS & CIBIL*

*CIBIL Score:* ${cibil || '___________'}

*Existing Loans:*
${activeLoansText || `1. Finance Company Name: ___________
2. Loan Type: ___________
3. Loan Amount (₹): ___________
4. EMI (₹): ___________
5. Tenure: ___________
6. Outstanding Amount (₹): ___________`}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📍 *REQUEST:*
Please check the above information and confirm the eligible loan amount and tenure, if doable.
Kindly revert at the earliest possible.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📞 *Contact Details:*
🏢 *Vardhaman Financial Services*
☎️ Office - 9270355171`;
    };

    const generateCommercialVehicleLoanProposal = (vehicleType = 'new') => {
        const vehicle = customer.vehicle_info || vehicleData || {};
        const basic = customer.customer_details?.basic_info || {};
        const occupation = customer.customer_details?.occupation_info || {};
        const business = occupation?.employment_details || {}; // Business details are in employment_details
        const residence = customer.customer_details?.residence_info || {};
        const existingLoans = customer.existing_loans || {};
        const loanApp = customer.loan_application?.loan_details || {};
        const vehicleHistory = customer.vehicle_history || { vehicles: [] };

        // Extract vehicle history data (handle both array and object formats)
        const getVehicleHistoryData = () => {
            const vehicles = vehicleHistory.vehicles;
            if (Array.isArray(vehicles) && vehicles.length > 0) {
                return vehicles[0];
            }
            if (vehicles && typeof vehicles === 'object') {
                const firstVehicle = Object.values(vehicles)[0];
                return firstVehicle || {};
            }
            return {};
        };
        const firstVehicle = getVehicleHistoryData();

        // Determine vehicle type based on parameter
        const vehicleTypeText = vehicleType === 'used' ? 'Used Commercial Vehicle' : 'New Commercial Vehicle';

        // Get active loans
        const activeLoansData = (existingLoans.active_loans && typeof existingLoans.active_loans === 'object')
            ? Object.values(existingLoans.active_loans)
            : [];

        // CIBIL Score
        let cibil = existingLoans.cibil_score || customer.cibil_score || '';

        // Format active loans
        let activeLoansText = '';
        if (activeLoansData.length > 0) {
            activeLoansData.forEach((loan, idx) => {
                activeLoansText += `
*Loan ${idx + 1}:*
1. Finance Company Name: ${loan.bankName || loan.bank || ''}
2. Loan Type: ${loan.loanType || loan.type || ''}
3. Loan Amount (₹): ${loan.loanAmount || loan.amount || ''}
4. EMI (₹): ${loan.emi || ''}
5. Tenure: ${loan.tenure || ''}
6. Outstanding Amount (₹): ${loan.outstandingAmount || loan.outstanding || loan.paidAmount || ''}
`;
            });
        }

        // Income details based on occupation type - only show if data exists
        let incomeSection = '';
        if (occupation.type === 'Job' || occupation.type === 'Salaried' || occupation.type === 'salaried') {
            const job = occupation.employment_details || {};
            // Only show if at least one field has data
            if (job.company_name || job.designation || job.years_employed || job.work_experience || job.monthly_salary || job.gross_salary) {
                incomeSection = `*Nature of Income:* Salaried

1. Company Name: ${job.company_name || ''}
2. Designation: ${job.designation || ''}
3. Work Experience (Years): ${job.years_employed || job.work_experience || ''}
4. Job Type: ${job.job_type || 'Permanent / Temporary'}
5. Gross Salary (₹): ${job.gross_salary || ''}
6. Net Salary (₹): ${job.monthly_salary || job.net_salary || ''}
7. Form 16: ${job.has_form16 ? 'Yes' : 'No'}`;
            }
        } else if (occupation.type === 'Business' || occupation.type === 'Self Employed' || occupation.type === 'self_employed') {
            // Only show if at least one field has data
            if (business.business_name || business.business_type || business.years_in_business || business.yearly_income || business.itr_income || business.yearly_turnover) {
                incomeSection = `*Nature of Income:* Business

1. Name of Business: ${business.business_name || ''}
2. Type of Business: ${business.business_type || ''}
3. Business Experience (Years): ${business.years_in_business || ''}
4. Business Premises: ${business.business_place_type || 'Rented / Owned'}
5. Yearly Income (₹): ${business.itr_income || business.yearly_income || business.yearly_turnover || ''}
6. ITR (Last 3 Years): ${business.itr_filed ? 'Yes' : 'No'}
7. Udyam Registration: ${business.has_udyam_reg ? 'Yes' : 'No'}
8. Shop Act License: ${business.has_shop_act ? 'Yes' : 'No'}`;
            }
        }

        // Agriculture income - only show if has farm data
        const farmIncome = customer.customer_details?.farm_income_info || {};
        const hasAgriIncome = farmIncome.has_farm || false;
        const agriSection = hasAgriIncome ? `

*Agriculture Income:* Yes
1. Land Area: ${farmIncome.farm_area || farmIncome.total_area || ''}
2. Owner Name: ${farmIncome.farm_owner_name || farmIncome.owner_name || ''}` : '';

        return `🚚 *LOAN PROPOSAL - COMMERCIAL VEHICLE* 🚚

*Vehicle Type:* ${vehicleTypeText}${vehicleType === 'used' && loanApp.loan_purpose ? `
*Purpose:* ${loanApp.loan_purpose}` : ''}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🚚 *VEHICLE DETAILS*
${vehicleType === 'used' ? `
1. *Registration Number:* ${vehicle.registration_number || vehicle.vehicleRegistrationNumber || '___________'}
2. *Vehicle Name:* ${vehicle.new_vehicle_type || vehicle.model || vehicle.vehicleModel || '___________'}
3. *Vehicle Model:* ${vehicle.new_vehicle_model || vehicle.model || vehicle.vehicleModel || '___________'}
4. *Manufacturing Year:* ${vehicle.manufacturing_year || vehicle.vehicleManufacturingYear || '___________'}
5. *Fuel Type:* ${vehicle.fuel_type || 'Petrol / Diesel / Petrol + CNG'}
6. *Number of Owners:* ${vehicle.number_of_owners || vehicle.vehicleNumberOfOwners || '___________'}
7. *Purchase Price (₹):* ${vehicle.purchase_price || vehicle.vehiclePurchasePrice || vehicle.new_vehicle_invoice_price || vehicle.new_vehicle_total || '___________'}
8. *Loan Amount Required (₹):* ${vehicle.loan_amount_required || vehicle.vehicleLoanAmountRequired || loanApp.loan_amount || '___________'}

9. *Hypothecation:* ${vehicle.hypothecation === 'yes' || vehicle.has_hypothecation ? 'Yes' : vehicle.hypothecation === 'no' ? 'No' : 'Yes / No'}
${vehicle.hypothecation === 'yes' || vehicle.has_hypothecation ? `   • *Bank / Finance Name:* ${vehicle.hypothecation_bank_name || '___________'}
   • *Balance Amount (₹):* ${vehicle.hypothecation_balance_amount || '___________'}` : `   • *Bank / Finance Name:* ___________
   • *Balance Amount (₹):* ___________`}

10. *Insurance:* ${vehicle.insurance === 'yes' || vehicle.new_vehicle_insurance ? 'Yes' : vehicle.insurance === 'no' ? 'No' : 'Yes / No'}
${vehicle.insurance === 'yes' || vehicle.new_vehicle_insurance ? `   • *IDV Amount (₹):* ${vehicle.insurance_idv_amount || vehicle.new_vehicle_insurance || '___________'}` : `   • *IDV Amount (₹):* ___________`}

11. *Fitness:* ${vehicle.has_fitness ? 'Yes' : 'Yes / No'}` : `
1. *Vehicle Name:* ${vehicle.new_vehicle_type || vehicle.model || '___________'}
2. *Vehicle Model:* ${vehicle.new_vehicle_model || vehicle.model || '___________'}
3. *Fuel Type:* ${vehicle.fuel_type || 'Petrol / Diesel / Petrol + CNG'}
4. *Purchase Price (₹):* ${vehicle.purchase_price || vehicle.new_vehicle_invoice_price || vehicle.new_vehicle_total || '___________'}

5. *Hypothecation:* ${vehicle.hypothecation === 'yes' || vehicle.has_hypothecation ? 'Yes' : vehicle.hypothecation === 'no' ? 'No' : 'Yes / No'}
${vehicle.hypothecation === 'yes' || vehicle.has_hypothecation ? `   • *Bank / Finance Name:* ${vehicle.hypothecation_bank_name || '___________'}
   • *Balance Amount (₹):* ${vehicle.hypothecation_balance_amount || '___________'}` : `   • *Bank / Finance Name:* ___________
   • *Balance Amount (₹):* ___________`}

6. *Insurance:* ${vehicle.insurance === 'yes' || vehicle.new_vehicle_insurance ? 'Yes' : vehicle.insurance === 'no' ? 'No' : 'Yes / No'}
${vehicle.insurance === 'yes' || vehicle.new_vehicle_insurance ? `   • *IDV Amount (₹):* ${vehicle.insurance_idv_amount || vehicle.new_vehicle_insurance || '___________'}` : `   • *IDV Amount (₹):* ___________`}

7. *Fitness:* ${vehicle.has_fitness ? 'Yes' : 'Yes / No'}`}


━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

👤 *CUSTOMER DETAILS*

1. *City:* ${basic.city_village || customer.city || '___________'}
2. *House Ownership:* ${residence.ownership_type || 'Owned / Rented'}
${residence.ownership_type === 'Owned' || residence.ownership_type === 'Own' ? `   *Owner Name:* ${residence.house_owner_name || '___________'}` : ''}
3. *Address Different from Aadhaar:* ${residence.different_from_aadhar ? 'Yes' : 'Yes / No'}
4. *Driving License Type:* ${vehicle.has_tr_license ? 'TR' : vehicle.has_heavy_license ? 'Heavy' : 'TR / Heavy'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

💼 *INCOME DETAILS*

${incomeSection}
${agriSection}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🚛 *OLD VEHICLE HISTORY*

1. *Vehicle Name:* ${firstVehicle?.model || '___________'}
2. *Finance Company Name:* ${firstVehicle?.financeCompany || '___________'}
3. *Loan Active?:* ${firstVehicle?.loanActive || '___________'}


━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

💳 *ACTIVE LOANS & CIBIL*

*CIBIL Score:* ${cibil || '___________'}

*Existing Loans:*
${activeLoansText || `1. Finance Company Name: ___________
2. Loan Type: ___________
3. Loan Amount (₹): ___________
4. EMI (₹): ___________
5. Tenure: ___________
6. Outstanding Amount (₹): ___________`}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📍 *REQUEST:*
Please review the above details and confirm the eligible loan amount and tenure, if doable.
Kindly update at the earliest possible.

📞 *Contact Details:*
🏢 *Vardhaman Financial Services*
☎️ Office - 9270355171`;
    };

    const generateBusinessFinanceProposal = () => {
        const basic = customer.customer_details?.basic_info || {};
        const occupation = customer.customer_details?.occupation_info || {};
        const business = occupation?.employment_details || {};
        const residence = customer.customer_details?.residence_info || {};
        const existingLoans = customer.existing_loans || {};
        const loanApp = customer.loan_application?.loan_details || {};
        const assets = customer.assets || {};

        // Active Loans
        const activeLoansData = (existingLoans.active_loans && typeof existingLoans.active_loans === 'object')
            ? Object.values(existingLoans.active_loans)
            : [];

        // CIBIL Score
        let cibil = existingLoans.cibil_score || customer.cibil_score || '';

        // Format active loans
        let activeLoansText = '';
        if (activeLoansData.length > 0) {
            activeLoansData.forEach((loan, idx) => {
                activeLoansText += `1. Bank / Finance Company Name: ${loan.bankName || loan.bank || '___________'}
2. Loan Type: ${loan.loanType || loan.type || '___________'}
3. Loan Amount: ${loan.loanAmount || loan.amount || '___________'}
4. EMI: ${loan.emi || '___________'}
5. Tenure: ${loan.tenure || '___________'}
6. Outstanding Amount: ${loan.outstandingAmount || loan.outstanding || loan.paidAmount || '___________'}\n\n`;
            });
        }

        // Income Details
        let incomeSection = '';
        if (occupation.type === 'Job' || occupation.type === 'Salaried') {
            const job = occupation.employment_details || {};
            incomeSection = `Type: Salaried

If Salaried:
1. Company Name: ${job.company_name || '___________'}
2. Designation: ${job.designation || '___________'}
3. Job Experience: ${job.years_employed || job.work_experience || '___________'}
4. Job Type: ${job.job_type || 'Permanent / Temporary'}
5. Gross Salary: ${job.gross_salary || '___________'}
6. Net Salary: ${job.monthly_salary || job.net_salary || '___________'}
7. Form 16: ${job.has_form16 ? 'Yes' : 'No'}`;
        } else if (occupation.type === 'Business' || occupation.type === 'Self Employed') {
            // Business Details
            incomeSection = `Type: Business

If Business:
1. Business Name: ${business.business_name || '___________'}
2. Type of Business: ${business.business_type || '___________'}
3. Business Entity Type: ${business.business_entity_type || '___________'}
4. Business Experience: ${business.years_in_business || '___________'}
5. Business Place: ${business.business_place_type || 'Owned / Rented'}
6. Yearly Income: ${business.itr_income || business.yearly_income || '___________'}
7. Turnover: ₹ ${business.yearly_turnover || '_____'}
8. Stock Value: ₹ ${business.stock_valuation_amount || '_____'}
9. ITR (Last 3 Years): ${business.itr_filed ? 'Yes' : 'No'}
10. Udyam Registration: ${business.has_udyam_reg ? 'Yes' : 'No'}
11. Shop Act: ${business.has_shop_act ? 'Yes' : 'No'}
12. GST: ${business.has_gst ? 'Yes' : 'No'}`;
        }

        // Farm Income
        const farmIncome = customer.customer_details?.farm_income_info || {};
        const agriSection = `Agriculture Income: ${farmIncome.has_farm ? 'Yes' : 'No'}
If Yes:
1. Area: ${farmIncome.farm_area || '_____'}
2. Owner Name: ${farmIncome.farm_owner_name || '_____'}`;

        // Collateral Details (from assets or LAP details)
        const lap = customer.lap_details || {};
        const collateralSection = `Collateral Details
1. Property Type: ${lap.property_type || assets.property_type || 'Flat / Bungalow / Construction / Patra / Kaularu'}
2. Property Sector: ${lap.property_sector || 'City Survey / Grampanchayat'}
3. Property Area: ${lap.plot_area || assets.plot || '_____'} Acre
4. Construction Area: ${lap.construction_area || '_____'} Sq. Ft.
5. Loan Requirement: ₹ ${loanApp.loan_amount || '_____'}
6. Agreement Value: ₹ ${lap.agreement_value || '_____'}
7. Market Value: ₹ ${lap.market_value || assets.home || '_____'}`;


        return `💼 *Business Finance – Loan Details* 💼

*Loan Type:*
${loanApp.loan_subcategory || 'CC / OD / Machinery Loan / Project Loan'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

👤 *Customer Details*
1. *City:* ${basic.city_village || customer.city || '___________'}
2. *House Ownership:* ${residence.ownership_type || 'Owned / Rented'}
(If Owned – Owner Name: ${residence.house_owner_name || '___________'})
3. *Address different from Aadhaar:* ${residence.different_from_aadhar ? 'Yes' : 'No'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

💼 *Income Details*
${incomeSection}

${agriSection}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🏠 *Collateral Details*
1. *Property Type:* ${lap.property_type || assets.property_type || 'Flat / Bungalow / Construction / Patra / Kaularu'}
2. *Property Sector:* ${lap.property_sector || 'City Survey / Grampanchayat'}
3. *Property Area:* ${lap.plot_area || assets.plot || '_____'} Acre
4. *Construction Area:* ${lap.construction_area || '_____'} Sq. Ft.
5. *Loan Requirement:* ₹ ${loanApp.loan_amount || '_____'}
6. *Agreement Value:* ₹ ${lap.agreement_value || '_____'}
7. *Market Value:* ₹ ${lap.market_value || assets.home || '_____'}${collateralSection.length > 0 ? '' : ''}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

💳 *Active Loans & CIBIL*
*CIBIL Score:* ${cibil || '_____'}

*Existing Loans:*
${activeLoansText || `1. Bank / Finance Company Name:
2. Loan Type:
3. Loan Amount:
4. EMI:
5. Tenure:
6. Outstanding Amount:`}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📝 *Remarks:* ${loanApp.remark || ''}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📍 *Request:*
Please review the above details and confirm the possible loan amount and tenure, if doable.
Kindly reply at the earliest.

📞 *Contact Details:*
🏢 *Vardhaman Financial Services*
☎️ Office - 9270355171`;
    };


    const generateMessage = () => {
        if (selectedTemplate === 'new_car') {
            return generateCarLoanProposal('new');
        }
        if (selectedTemplate === 'used_car') {
            return generateCarLoanProposal('used');
        }
        if (selectedTemplate === 'new_commercial_vehicle') {
            return generateCommercialVehicleLoanProposal('new');
        }
        if (selectedTemplate === 'used_commercial_vehicle') {
            return generateCommercialVehicleLoanProposal('used');
        }
        if (selectedTemplate === 'property') {
            return generatePropertyLoanMessage();
        }
        if (selectedTemplate === 'business_finance') {
            return generateBusinessFinanceProposal();
        }
        return generateDocumentMessage();
    };

    const message = generateMessage();

    // Load all contacts and vehicle data when component mounts


    // Helper function to calculate age from birthdate
    const calculateAge = (birthdate) => {
        if (!birthdate) return '';
        const today = new Date();
        const birthDate = new Date(birthdate);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const loadVehicleData = async () => {
        try {
            if (!database || !customer.id) return;

            // Try to fetch from vehicles node
            const vehiclesRef = ref(database, 'vehicles');
            const vehiclesSnapshot = await get(vehiclesRef);

            if (vehiclesSnapshot.exists()) {
                const vehiclesData = vehiclesSnapshot.val();
                // Find vehicle matching customer ID
                const customerVehicle = Object.values(vehiclesData).find(
                    vehicle => vehicle.customerId === customer.id || vehicle.customerId === String(customer.id)
                );

                if (customerVehicle) {
                    setVehicleData(customerVehicle);
                    console.log('Vehicle data loaded:', customerVehicle);
                } else {
                    console.log('No vehicle found for customer ID:', customer.id);
                }
            }
        } catch (error) {
            console.error('Error loading vehicle data:', error);
        }
    };


    const loadAllContacts = async () => {
        setLoadingContacts(true);
        try {
            const contacts = [];

            // 1. Load Customers from Firestore
            const { collection, getDocs } = await import('firebase/firestore');
            const { db } = await import('../firebase');

            const customersRef = collection(db, 'customers');
            const customersSnapshot = await getDocs(customersRef);

            customersSnapshot.docs.forEach(doc => {
                const data = doc.data();
                if (data.mobile1 && data.customerId !== customer.id) {
                    contacts.push({
                        id: `customer_${data.customerId}`,
                        name: data.fullName || 'Unknown Customer',
                        mobile: data.mobile1,
                        type: 'customer',
                        category: 'Customers',
                        icon: '👤'
                    });
                }
            });

            // 2. Load All Contacts from Realtime Database (agents, bankers, dealers)
            if (database) {
                const contactsRef = ref(database, 'contacts');
                const contactsSnapshot = await get(contactsRef);

                if (contactsSnapshot.exists()) {
                    const contactsData = contactsSnapshot.val();

                    // Helper to map dealerType to Category and Icon
                    const getContactInfo = (type, defaultCategory, defaultIcon) => {
                        const normalizedType = (type || '').toLowerCase();
                        switch (normalizedType) {
                            case 'dealer': return { category: 'Dealers', icon: '🏪' };
                            case 'agent': return { category: 'Agents', icon: '🧑‍💼' };
                            case 'banker': return { category: 'Bankers', icon: '🏦' };
                            case 'finance_executive': return { category: 'Finance Executive/Banker', icon: '💼' };
                            case 'customer': return { category: 'Customers', icon: '👥' };
                            case 'key_person': return { category: 'Key Person', icon: '⭐' };
                            case 'dsa': return { category: 'DSA', icon: '🔗' };
                            case 'bni': return { category: 'BNI', icon: '🤝' };
                            case 'social_media': return { category: 'Social Media', icon: '📱' };
                            case 'others':
                            case 'other': return { category: 'Others', icon: '✨' };
                            default: return { category: defaultCategory, icon: defaultIcon };
                        }
                    };

                    // Iterate through each group (agents, bankers, dealers)
                    Object.entries(contactsData).forEach(([groupName, groupData]) => {
                        if (typeof groupData === 'object') {
                            Object.entries(groupData).forEach(([id, person]) => {
                                if ((person.mobile1 || person.phone || person.mobile) && person.isActive !== false) {

                                    // Determine default category based on group name
                                    let defaultCat = 'Dealers';
                                    let defaultIcon = '🏪';
                                    if (groupName === 'agents') { defaultCat = 'Agents'; defaultIcon = '🧑‍💼'; }
                                    if (groupName === 'bankers') { defaultCat = 'Bankers'; defaultIcon = '🏦'; }

                                    // Use dealerType if available, otherwise fallback to group default
                                    const { category, icon } = getContactInfo(person.dealerType, defaultCat, defaultIcon);

                                    contacts.push({
                                        id: `${groupName}_${person.dealerId || person.agentId || person.bankerId || id}`,
                                        name: person.dealerName || person.agentName || person.bankerName || person.name || person.fullName || 'Unknown',
                                        mobile: person.mobile1 || person.phone || person.mobile,
                                        type: person.dealerType || groupName.slice(0, -1), // remove 's' for type
                                        category: category,
                                        icon: icon,
                                        businessName: person.businessName || person.companyName || person.company || person.bankName || person.bank
                                    });
                                }
                            });
                        }
                    });
                }
            }

            // 3. Load Employees
            if (database) {
                const employeesRef = ref(database, 'employees');
                const employeesSnapshot = await get(employeesRef);

                if (employeesSnapshot.exists()) {
                    const employeesData = employeesSnapshot.val();
                    Object.keys(employeesData).forEach(id => {
                        const employee = employeesData[id];
                        if (employee.mobile && employee.isActive) {
                            contacts.push({
                                id: `employee_${employee.employeeId}`,
                                name: employee.fullName || 'Unknown Employee',
                                mobile: employee.mobile,
                                type: 'employee',
                                category: 'Employees',
                                icon: '👨‍💼',
                                businessName: 'Internal Team'
                            });
                        }
                    });
                }
            }

            console.log('Contacts Loaded:', contacts);
            setAllContacts(contacts);
        } catch (error) {
            console.error('Error loading contacts:', error);
        } finally {
            setLoadingContacts(false);
        }
    };

    // Load all contacts and vehicle data when component mounts
    useEffect(() => {
        loadAllContacts();
        loadVehicleData();
    }, []);

    const handleContactToggle = (contactId) => {
        setSelectedContacts(prev =>
            prev.includes(contactId)
                ? prev.filter(id => id !== contactId)
                : [...prev, contactId]
        );
    };

    const handleSelectAllContacts = () => {
        const filteredContacts = getFilteredContacts();
        if (selectedContacts.length === filteredContacts.length) {
            setSelectedContacts([]);
        } else {
            setSelectedContacts(filteredContacts.map(contact => contact.id));
        }
    };

    const getFilteredContacts = () => {
        return allContacts.filter(contact => {
            const matchesSearch = contact.name.toLowerCase().includes(contactSearchTerm.toLowerCase()) ||
                contact.mobile.includes(contactSearchTerm) ||
                (contact.businessName && contact.businessName.toLowerCase().includes(contactSearchTerm.toLowerCase()));

            const matchesType = contactTypeFilter === 'all' || contact.category === contactTypeFilter;

            return matchesSearch && matchesType;
        });
    };

    const handleSendToSelectedContacts = () => {
        const encodedMessage = encodeURIComponent(message);

        // Open all tabs immediately to avoid popup blocking
        selectedContacts.forEach((contactId) => {
            const contact = allContacts.find(c => c.id === contactId);
            if (contact) {
                const phoneNumber = contact.mobile.replace(/[^0-9]/g, '');
                const whatsappUrl = `https://web.whatsapp.com/send?phone=${phoneNumber}&text=${encodedMessage}`;

                // Open immediately - no setTimeout to avoid popup blocking
                window.open(whatsappUrl, '_blank');
            }
        });

        // Show success message
        alert(`✅ Opening ${selectedContacts.length} WhatsApp tabs!\n\nEach tab will have the message pre-filled. Just click send in each tab.`);

        // Close contact card
        setShowContactCard(false);
    };

    const handleSendOneByOne = () => {
        if (selectedContacts.length === 0) return;

        const encodedMessage = encodeURIComponent(message);
        let currentIndex = 0;

        const sendNext = () => {
            if (currentIndex < selectedContacts.length) {
                const contact = allContacts.find(c => c.id === selectedContacts[currentIndex]);
                if (contact) {
                    const phoneNumber = contact.mobile.replace(/[^0-9]/g, '');
                    const whatsappUrl = `https://web.whatsapp.com/send?phone=${phoneNumber}&text=${encodedMessage}`;
                    window.open(whatsappUrl, '_blank');

                    currentIndex++;

                    if (currentIndex < selectedContacts.length) {
                        // Ask user if they want to continue to next contact
                        setTimeout(() => {
                            const continueNext = window.confirm(`Sent to ${contact.name}!\n\nContinue to next contact (${currentIndex + 1}/${selectedContacts.length})?`);
                            if (continueNext) {
                                sendNext();
                            }
                        }, 2000);
                    } else {
                        alert('✅ All messages sent!');
                    }
                }
            }
        };

        sendNext();
        setShowContactCard(false);
    };

    // Get selected contacts for WhatsApp API (only selected contacts, customer is optional)
    const getSelectedContactsForAPI = () => {
        const contacts = [];

        // Add all selected contacts from the unified contact selection
        selectedContacts.forEach(contactId => {
            // Check if this is the customer contact
            if (contactId === `customer_${customer.id}`) {
                // Add customer contact directly
                if (customer.mobile1) {
                    contacts.push({
                        id: contactId,
                        name: customer.name,
                        mobile: customer.mobile1,
                        type: 'customer',
                        businessName: null
                    });
                }
            } else {
                // Find contact in allContacts array
                const contact = allContacts.find(c => c.id === contactId);
                if (contact && contact.mobile) {
                    contacts.push({
                        id: contact.id,
                        name: contact.name,
                        mobile: contact.mobile,
                        type: contact.type,
                        businessName: contact.businessName
                    });
                }
            }
        });

        return contacts;
    };

    // Direct send function using WhatsApp API
    const handleDirectSend = async () => {
        if (selectedContacts.length === 0) {
            alert('Please select at least one contact');
            return;
        }

        console.log('🚀 Starting direct send...', {
            selectedContactsCount: selectedContacts.length,
            message: message
        });

        setSending(true);
        setSendProgress({ sent: 0, total: selectedContacts.length, current: '' });
        setSendResults([]);

        const results = [];
        const contactsToSend = getSelectedContactsForAPI();

        console.log('📋 Contacts to send:', contactsToSend);

        for (let i = 0; i < contactsToSend.length; i++) {
            const contact = contactsToSend[i];
            console.log(`📤 Sending to contact ${i + 1}/${contactsToSend.length}:`, contact);

            setSendProgress({
                sent: i,
                total: contactsToSend.length,
                current: contact.name
            });

            try {
                const formattedPhone = whatsappAPI.formatPhoneNumber(contact.mobile);
                const formattedMessage = whatsappAPI.formatMessage(message);

                console.log('📞 Formatted phone:', formattedPhone);
                console.log('💬 Formatted message:', formattedMessage);

                const result = await whatsappAPI.sendMessage(formattedPhone, formattedMessage);

                console.log('✅ Send result:', result);

                results.push({
                    contact: contact,
                    success: result.success === true,
                    error: result.error || (result.success ? null : 'Unknown error'),
                    messageId: result.messageId
                });

                // Add delay between messages to avoid rate limiting
                if (i < contactsToSend.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 2000));
                }
            } catch (error) {
                console.error('❌ Error sending message:', error);
                results.push({
                    contact: contact,
                    success: false,
                    error: error.message || 'Failed to send message'
                });
            }
        }

        console.log('📊 Final results:', results);

        setSendProgress({ sent: contactsToSend.length, total: contactsToSend.length, current: '' });
        setSendResults(results);
        setSending(false);

        // Show summary
        const successCount = results.filter(r => r.success).length;
        const failCount = results.filter(r => !r.success).length;

        alert(`✅ Sending Complete!\n\nSuccess: ${successCount}\nFailed: ${failCount}\n\nCheck the results below for details.`);
    };




    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(message);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    return (
        <div className="mt-4 sm:mt-6 p-3 sm:p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border-2 border-green-200">
            {/* Header */}
            <div className="flex flex-col gap-3 mb-4">
                <div className="flex items-center justify-between gap-2">
                    <h3 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2 flex-shrink-0">
                        <span className="text-xl sm:text-2xl">📱</span>
                        <span>WhatsApp Actions</span>
                    </h3>
                </div>
            </div>

            {/* Template Selector */}
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-2">
                <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">Template:</label>
                <select
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none transition-all text-sm font-medium w-full"
                >
                    <option value="documents">📄 Document Request</option>
                    <option value="new_car">🚗 New Car Loan Proposal</option>
                    <option value="used_car">🚙 Used Car Loan Proposal</option>
                    <option value="new_commercial_vehicle">🚚 New Commercial Vehicle Loan Proposal</option>
                    <option value="used_commercial_vehicle">🚛 Used Commercial Vehicle Loan Proposal</option>
                    <option value="property">🏠 Property Loan Proposal</option>
                    <option value="business_finance">💼 Business Finance Proposal</option>
                </select>
            </div>

            {/* Message Preview */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden mb-4">
                <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold flex items-center gap-2">
                    <span className="text-base sm:text-xl">📱</span>
                    <span className="text-sm sm:text-base">WhatsApp Message Preview</span>
                </div>

                {/* Other Person Information — display only, no sending */}
                {customer.otherPerson && customer.otherPerson.fullName && (
                    <div className="mx-3 sm:mx-6 mt-4 mb-2 rounded-xl border border-purple-200 bg-purple-50 overflow-hidden">
                        <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                            <span className="text-base">👥</span>
                            <span className="text-sm font-semibold">Other Person Information</span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 px-4 py-3">
                            {customer.otherPerson.relationship && (
                                <div>
                                    <p className="text-xs font-semibold text-purple-500 uppercase tracking-wide mb-0.5">Relationship</p>
                                    <p className="text-sm font-medium text-gray-800">{customer.otherPerson.relationship}</p>
                                </div>
                            )}
                            <div>
                                <p className="text-xs font-semibold text-purple-500 uppercase tracking-wide mb-0.5">Full Name</p>
                                <p className="text-sm font-medium text-gray-800">{customer.otherPerson.fullName}</p>
                            </div>
                            {customer.otherPerson.mobile && (
                                <div>
                                    <p className="text-xs font-semibold text-purple-500 uppercase tracking-wide mb-0.5">Mobile Number</p>
                                    <p className="text-sm font-medium text-gray-800">📱 {customer.otherPerson.mobile}</p>
                                </div>
                            )}
                            {customer.otherPerson.dateOfBirth && (
                                <div>
                                    <p className="text-xs font-semibold text-purple-500 uppercase tracking-wide mb-0.5">Date of Birth</p>
                                    <p className="text-sm font-medium text-gray-800">
                                        🎂 {new Date(customer.otherPerson.dateOfBirth).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </p>
                                </div>
                            )}
                            {customer.otherPerson.remarks && (
                                <div className="col-span-2 sm:col-span-3">
                                    <p className="text-xs font-semibold text-purple-500 uppercase tracking-wide mb-0.5">Remarks</p>
                                    <p className="text-sm font-medium text-gray-800">{customer.otherPerson.remarks}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <pre className="p-3 sm:p-6 text-xs sm:text-sm text-gray-800 whitespace-pre-wrap font-sans max-h-64 sm:max-h-96 overflow-y-auto">
                    {message}
                </pre>
            </div>

            {/* Actions */}
            <div className="space-y-3">
                {/* All Actions in Single Row */}
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={handleCopy}
                        className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-gradient-to-r from-primary-500 to-primary-700 text-white rounded-lg font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 text-sm"
                    >
                        <span className="text-base">{copied ? '✓' : '📋'}</span>
                        <span>{copied ? 'Copied!' : 'Copy Message'}</span>
                    </button>

                    <button
                        onClick={() => {
                            setShowDealerSelection(!showDealerSelection);
                            if (!showDealerSelection) setShowAPIIntegration(false);
                        }}
                        className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 text-sm"
                    >
                        <span className="text-base">👥</span>
                        <span>Select Contacts ({selectedContacts.length} selected)</span>
                    </button>
                </div>
            </div>

            {/* Contact Selection Panel */}
            {showDealerSelection && (
                <div className="mt-4 p-3 sm:p-6 bg-white rounded-xl shadow-lg border-2 border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                            <span className="text-2xl">👥</span>
                            Select Contacts
                        </h4>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleDirectSend}
                                disabled={selectedContacts.length === 0 || sending}
                                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                            >
                                <span className="text-lg">{sending ? '⏳' : '🤖'}</span>
                                <span>{sending ? `Sending... (${sendProgress.sent}/${sendProgress.total})` : `Send (${selectedContacts.length})`}</span>
                            </button>
                            <button
                                onClick={() => setShowDealerSelection(false)}
                                className="text-gray-500 hover:text-gray-700 text-xl"
                            >
                                ✕
                            </button>
                        </div>
                    </div>

                    {/* Customer Info (Optional - with checkbox) */}
                    <div
                        onClick={() => handleContactToggle(`customer_${customer.id}`)}
                        className={`mb-4 p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedContacts.includes(`customer_${customer.id}`)
                            ? 'bg-green-50 border-green-300'
                            : 'bg-gray-50 border-gray-200 hover:border-green-300'
                            }`}
                    >
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                checked={selectedContacts.includes(`customer_${customer.id}`)}
                                onChange={() => { }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-5 h-5 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                            />
                            <span className="text-lg">👤</span>
                            <div className="flex-1">
                                <div className="font-semibold text-gray-800">{customer.name}</div>
                                <div className="text-sm text-gray-600">📱 {customer.mobile1}</div>
                            </div>
                        </div>
                    </div>


                    {/* Filter & Search Bar */}
                    <div className="mb-4 flex gap-3 items-center">
                        <div className="w-1/3">
                            <select
                                value={contactTypeFilter}
                                onChange={(e) => setContactTypeFilter(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none bg-white text-gray-700"
                            >
                                <option value="all">All Types</option>
                                <option value="Dealers">Dealer</option>
                                <option value="Agents">Agent</option>
                                <option value="Finance Executive/Banker">Finance Executive/Banker</option>
                                <option value="Customers">Customer</option>
                                <option value="Key Person">Key Person</option>
                                <option value="DSA">DSA</option>
                                <option value="BNI">BNI</option>
                                <option value="Social Media">Social Media</option>
                                <option value="Others">Others</option>
                                <option value="Bankers">Banker</option>
                                <option value="Employees">Employees</option>
                            </select>
                        </div>
                        <div className="relative flex-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                            <input
                                type="text"
                                placeholder="Search contacts..."
                                value={contactSearchTerm}
                                onChange={(e) => setContactSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none transition-all"
                            />
                        </div>
                        <button
                            onClick={handleSelectAllContacts}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors whitespace-nowrap"
                        >
                            <input
                                type="checkbox"
                                checked={selectedContacts.length === getFilteredContacts().length && getFilteredContacts().length > 0}
                                onChange={() => { }}
                                className="w-4 h-4"
                            />
                            <span className="font-semibold text-gray-700">
                                Select All ({getFilteredContacts().length} contacts)
                            </span>
                        </button>
                    </div>

                    {loadingContacts ? (
                        <div className="text-center py-4">
                            <div className="w-8 h-8 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-2"></div>
                            <p className="text-gray-600">Loading contacts...</p>
                        </div>
                    ) : getFilteredContacts().length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <p className="text-lg">No contacts found</p>
                        </div>
                    ) : (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {/* Group contacts by category */}
                            {['Customers', 'Agents', 'Bankers', 'Dealers', 'Finance Executive/Banker', 'Key Person', 'DSA', 'BNI', 'Social Media', 'Others', 'Employees'].map(category => {
                                const categoryContacts = getFilteredContacts().filter(contact => contact.category === category);
                                if (categoryContacts.length === 0) return null;

                                return (
                                    <div key={category}>
                                        <h5 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                                            {category} ({categoryContacts.length})
                                        </h5>
                                        <div className="space-y-2 mb-4">
                                            {categoryContacts.map(contact => (
                                                <div
                                                    key={contact.id}
                                                    onClick={() => handleContactToggle(contact.id)}
                                                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${selectedContacts.includes(contact.id)
                                                        ? 'bg-purple-50 border-purple-300'
                                                        : 'bg-gray-50 border-gray-200 hover:border-purple-200'
                                                        }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedContacts.includes(contact.id)}
                                                        onChange={() => { }}
                                                        className="w-4 h-4 text-purple-600"
                                                    />
                                                    <span className="text-lg">{contact.icon}</span>
                                                    <div className="flex-1">
                                                        <div className="font-semibold text-gray-800">{contact.name}</div>
                                                        <div className="text-sm text-gray-600">📱 {contact.mobile}</div>
                                                        {contact.businessName && (
                                                            <div className="text-xs text-gray-500">{contact.businessName}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}



                </div>
            )}

            {/* Sending Progress */}
            {sending && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-blue-800">Sending Messages...</span>
                        <span className="text-sm text-blue-600">
                            {sendProgress.sent}/{sendProgress.total}
                        </span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
                        <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(sendProgress.sent / sendProgress.total) * 100}%` }}
                        ></div>
                    </div>
                    {sendProgress.current && (
                        <p className="text-sm text-blue-700">
                            Currently sending to: {sendProgress.current}
                        </p>
                    )}
                </div>
            )}

            {/* Send Results */}
            {sendResults.length > 0 && !sending && (
                <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-3">Send Results:</h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                        {sendResults.map((result, index) => (
                            <div
                                key={index}
                                className={`flex items-center justify-between p-2 rounded ${result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                    }`}
                            >
                                <span className="font-medium">
                                    {result.contact?.name || 'Unknown'}
                                </span>
                                <span className="text-sm">
                                    {result.success ? '✅ Sent' : `❌ ${result.error}`}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-300">
                        <div className="flex justify-between text-sm">
                            <span>Success: {sendResults.filter(r => r.success).length}</span>
                            <span>Failed: {sendResults.filter(r => !r.success).length}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Compact Contact Card */}
            {showContactCard && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden mx-4">
                        {/* Header */}
                        <div className="px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="text-2xl">📱</span>
                                <h3 className="text-lg font-bold">Send WhatsApp Message</h3>
                            </div>
                            <button
                                onClick={() => setShowContactCard(false)}
                                className="text-white hover:text-gray-200 text-xl"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Message Status */}
                        <div className="px-6 py-3 bg-green-50 border-b">
                            <p className="text-sm text-green-800 flex items-center gap-2">
                                <span>✅</span>
                                <span>Message copied to clipboard!</span>
                            </p>
                        </div>

                        {/* Contact List */}
                        <div className="p-6 max-h-96 overflow-y-auto">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="font-semibold text-gray-800">Select Contacts to Send:</h4>
                                <span className="text-sm text-gray-600">
                                    {selectedContacts.length} selected
                                </span>
                            </div>

                            {/* Filter & Search Bar */}
                            <div className="flex gap-2 mb-4">
                                <div className="w-1/3">
                                    <select
                                        value={contactTypeFilter}
                                        onChange={(e) => setContactTypeFilter(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none bg-white text-gray-700"
                                    >
                                        <option value="all">All Types</option>
                                        <option value="Customers">Customers</option>
                                        <option value="Agents">Agents</option>
                                        <option value="Bankers">Bankers</option>
                                        <option value="Dealers">Dealers</option>
                                        <option value="Employees">Employees</option>
                                    </select>
                                </div>
                                <div className="w-2/3 relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                                    <input
                                        type="text"
                                        placeholder="Search contacts..."
                                        value={contactSearchTerm}
                                        onChange={(e) => setContactSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none"
                                    />
                                </div>
                            </div>

                            {/* Select All Button */}
                            <div className="mb-4">
                                <button
                                    onClick={handleSelectAllContacts}
                                    className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedContacts.length === getFilteredContacts().length && getFilteredContacts().length > 0}
                                        onChange={() => { }}
                                        className="w-4 h-4"
                                    />
                                    <span className="font-semibold text-gray-700">
                                        {selectedContacts.length === getFilteredContacts().length && getFilteredContacts().length > 0 ? 'Deselect All' : 'Select All'}
                                    </span>
                                </button>
                            </div>

                            {loadingContacts ? (
                                <div className="text-center py-8">
                                    <div className="w-8 h-8 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-2"></div>
                                    <p className="text-gray-600">Loading contacts...</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {/* Group contacts by category */}
                                    {['Customers', 'Agents', 'Bankers', 'Dealers', 'Employees'].map(category => {
                                        const categoryContacts = getFilteredContacts().filter(contact => contact.category === category);
                                        if (categoryContacts.length === 0) return null;

                                        return (
                                            <div key={category}>
                                                <h5 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                                                    {category} ({categoryContacts.length})
                                                </h5>
                                                <div className="space-y-2 mb-4">
                                                    {categoryContacts.map(contact => (
                                                        <div
                                                            key={contact.id}
                                                            onClick={() => handleContactToggle(contact.id)}
                                                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${selectedContacts.includes(contact.id)
                                                                ? 'bg-purple-50 border-purple-300'
                                                                : 'bg-gray-50 border-gray-200 hover:border-purple-200'
                                                                }`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedContacts.includes(contact.id)}
                                                                onChange={() => { }}
                                                                className="w-4 h-4 text-purple-600"
                                                            />
                                                            <span className="text-lg">{contact.icon}</span>
                                                            <div className="flex-1">
                                                                <div className="font-semibold text-gray-800">{contact.name}</div>
                                                                <div className="text-sm text-gray-600">📱 {contact.mobile}</div>
                                                                {contact.businessName && (
                                                                    <div className="text-xs text-gray-500">{contact.businessName}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Footer Actions */}
                        <div className="px-6 py-4 bg-gray-50 border-t">
                            <div className="space-y-3">
                                {/* Send to Selected Contacts Button */}
                                {selectedContacts.length > 0 && (
                                    <button
                                        onClick={handleSendToSelectedContacts}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
                                    >
                                        <span className="text-xl">🚀</span>
                                        <span>Send to All ({selectedContacts.length} contacts)</span>
                                    </button>
                                )}

                                {/* Alternative: Send One by One */}
                                {selectedContacts.length > 1 && (
                                    <button
                                        onClick={handleSendOneByOne}
                                        className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg text-sm transition-colors"
                                    >
                                        📤 Send One by One (if popups blocked)
                                    </button>
                                )}

                                {/* Other Actions */}
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => window.open('https://web.whatsapp.com/', '_blank')}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-semibold transition-colors"
                                    >
                                        <span>🌐</span>
                                        <span>Open WhatsApp Web</span>
                                    </button>
                                    <button
                                        onClick={() => setShowContactCard(false)}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
                                    >
                                        <span>✓</span>
                                        <span>Done</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* WhatsApp API Integration Panel */}
            {showAPIIntegration && (
                <div className="mt-4 p-6 bg-white rounded-xl shadow-lg border-2 border-green-200">
                    <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                            <span className="text-2xl">🤖</span>
                            WhatsApp API - Automated Sending
                        </h4>
                        <button
                            onClick={() => setShowAPIIntegration(false)}
                            className="text-gray-500 hover:text-gray-700 text-xl"
                        >
                            ✕
                        </button>
                    </div>

                    <WhatsAppAPIIntegration
                        message={message}
                        selectedContacts={getSelectedContactsForAPI()}
                        onSendComplete={(results) => {
                            console.log('Send completed:', results);
                            // Handle completion (show success message, etc.)
                        }}
                    />
                </div>
            )}

        </div>
    );
};

export default WhatsAppMessage;
